<template>
  <div class="print-page-container">
    <!-- Screen-only controls -->
    <div class="print-controls screen-only">
      <button class="print-button" @click="printPage">
        {{ adminText('organizationAll.print_report_23a04651', '🖨️ Print Report') }}
      </button>
      <button class="back-button" @click="goBack">
        {{ adminText('organizationAll.back_to_reports_65b7df06', '← Back to Reports') }}
      </button>
    </div>

    <div v-if="!loading" class="print-wrapper">
      <header class="print-header">
        <div class="print-header__left">
          <img src="/assets/color-logo.svg" style="width: 100px; height: auto" :alt="adminText('organizationAll.coreevent_1e8bf811', 'CoreEvent')">
          <div class="print-header__meta">
            <span>{{ organizationName }}</span>
            <span class="print-header__period">{{ adminText('organizationAll.period_ea2ad7a7', 'Period:') }} {{ formattedFrom }} – {{ formattedTo }}</span>
            <span v-if="scopeLabel" class="print-header__period">{{ scopeLabel }}</span>
          </div>
        </div>
        <span class="print-header__generated">{{ adminText('organizationAll.generated_a2edf57c', 'Generated:') }} {{ formatDateTime(now) }}</span>
      </header>

      <section v-if="summary" class="summary-section">
        <h1>{{ adminText('organizationAll.subscription_finance_overview_81532ddc', 'Subscription Finance Overview') }}</h1>
        <dl>
          <div>
            <dt>{{ adminText('organizationAll.gross_revenue_bac54e11', 'Gross revenue') }}</dt>
            <dd>{{ formatCurrency(summary.grossAmount) }}</dd>
          </div>
          <div>
            <dt>{{ adminText('organizationAll.net_revenue_83e899da', 'Net revenue') }}</dt>
            <dd>{{ formatCurrency(summary.netAmount) }}</dd>
          </div>
          <div>
            <dt>{{ adminText('organizationAll.vat_5f54ad7b', 'VAT') }}</dt>
            <dd>{{ formatCurrency(summary.vatAmount) }}</dd>
          </div>
          <div>
            <dt>{{ adminText('organizationAll.online_gross_4fa936e9', 'Online gross') }}</dt>
            <dd>{{ formatCurrency(summary.onlineGrossAmount) }}</dd>
          </div>
          <div>
            <dt>{{ adminText('organizationAll.offline_gross_2779ef37', 'Offline gross') }}</dt>
            <dd>{{ formatCurrency(summary.offlineGrossAmount) }}</dd>
          </div>
          <div>
            <dt>{{ adminText('organizationAll.total_orders_b0cfa9c4', 'Total orders') }}</dt>
            <dd>{{ formatNumber(summary.orderCount) }}</dd>
          </div>
          <div>
            <dt>{{ adminText('organizationAll.subscriptions_sold_669557f7', 'Subscriptions sold') }}</dt>
            <dd>{{ formatNumber(summary.totalQuantity) }}</dd>
          </div>
        </dl>
      </section>

      <section v-if="rows.length" class="table-section">
        <h2>{{ adminText('organizationAll.ledger_breakdown_83989a0d', 'Ledger breakdown') }}</h2>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>{{ adminText('organizationAll.order_1d75774c', 'Order') }}</th>
              <th>{{ adminText('organizationAll.date_eb9a4bc1', 'Date') }}</th>
              <th>{{ adminText('organizationAll.plan_ae2f98a0', 'Plan') }}</th>
              <th>{{ adminText('organizationAll.quantity_44f6af69', 'Quantity') }}</th>
              <th>{{ adminText('organizationAll.unit_price_3c6c777f', 'Unit price') }}</th>
              <th>{{ adminText('organizationAll.gross_9580a617', 'Gross') }}</th>
              <th>{{ adminText('organizationAll.vat_5f54ad7b', 'VAT') }}</th>
              <th>{{ adminText('organizationAll.net_9bb81c2e', 'Net') }}</th>
              <th>{{ adminText('organizationAll.channel_879f0b1b', 'Channel') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="row.orderId + '-' + row.subscriptionId">
              <td>{{ index + 1 }}</td>
              <td>#{{ row.orderId }}</td>
              <td>{{ formatDate(row.createdAt) }}</td>
              <td>{{ row.subscriptionName }}</td>
              <td>{{ formatNumber(row.quantity) }}</td>
              <td>{{ formatCurrency(row.unitPrice) }}</td>
              <td>{{ formatCurrency(row.grossAmount) }}</td>
              <td>{{ formatCurrency(row.vatAmount) }}</td>
              <td>{{ formatCurrency(row.netAmount) }}</td>
              <td>{{ row.paymentChannel }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <p v-else class="empty-state">{{ adminText('organizationAll.no_ledger_records_found_for_the_selected_peri_88bb1dc8', 'No ledger records found for the selected period.') }}</p>
    </div>
    <div v-else class="loading-state">{{ adminText('organizationAll.loading_33ce4174', 'Loading…') }}</div>
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import moment from 'moment-timezone'
import {
  GET_THEATER_SUBSCRIPTION_FINANCE_LEDGER,
  GET_THEATER_SUBSCRIPTION_FINANCE_SUMMARY,
} from '@/graphql/theater-subscriptions.graphql'

const REPORT_TIMEZONE = 'Europe/Zagreb'

export default {
  mixins: [OrganizationLocalization],
  name: 'theater-subscription-reports-printable',
  props: {
    organizationSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      rows: [],
      summary: null,
      now: new Date(),
      loading: true,
      organizationName: '',
      fromIso: this.$route.query.from || null,
      toIso: this.$route.query.to || null,
      subscriptionId: this.$route.query.subscriptionId ? Number(this.$route.query.subscriptionId) : null,
      paymentChannel: this.$route.query.paymentChannel || null,
      subscriptionName: '',
    }
  },
  computed: {
    formattedFrom() {
      return this.fromIso ? this.formatDate(this.fromIso) : '—'
    },
    formattedTo() {
      return this.toIso ? this.formatDate(this.toIso) : '—'
    },
    scopeLabel() {
      const parts = []
      if (this.subscriptionId) {
        parts.push(`Subscription: ${this.subscriptionName || `#${this.subscriptionId}`}`)
      }
      if (this.paymentChannel) {
        parts.push(`Source: ${this.paymentChannel === 'OFFLINE' ? 'Box office' : 'Online'}`)
      }

      return parts.join(' · ')
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      try {
        const filters = {
          organizationSlug: this.organizationSlug,
        }
        if (this.fromIso) {
          filters.from = this.fromIso
        }
        if (this.toIso) {
          filters.to = this.toIso
        }
        if (this.subscriptionId) {
          filters.subscriptionId = this.subscriptionId
        }
        if (this.paymentChannel) {
          filters.paymentChannel = this.paymentChannel
        }

        const [summaryResponse, ledgerResponse] = await Promise.all([
          this.$apollo.query({
            query: GET_THEATER_SUBSCRIPTION_FINANCE_SUMMARY,
            variables: { filters },
            fetchPolicy: 'no-cache',
          }),
          this.$apollo.query({
            query: GET_THEATER_SUBSCRIPTION_FINANCE_LEDGER,
            variables: { filters },
            fetchPolicy: 'no-cache',
          }),
        ])

        const summaryData = summaryResponse && summaryResponse.data ? summaryResponse.data : null
        const ledgerData = ledgerResponse && ledgerResponse.data ? ledgerResponse.data : null

        this.summary = summaryData && summaryData.getTheaterSubscriptionFinanceSummary
          ? summaryData.getTheaterSubscriptionFinanceSummary
          : null

        if (this.summary) {
          this.organizationName = this.summary.organizationName || this.organizationSlug
          if (this.subscriptionId) {
            this.subscriptionName = this.summary.subscriptionName || ''
          }
        }

        this.rows = ledgerData && ledgerData.getTheaterSubscriptionFinanceLedger
          ? ledgerData.getTheaterSubscriptionFinanceLedger
          : []

        if (this.rows.length) {
          const firstRow = this.rows[0]
          this.organizationName =
            this.organizationName ||
            firstRow.organizationName ||
            firstRow.organizationSlug ||
            this.organizationSlug
          if (this.subscriptionId && !this.subscriptionName) {
            this.subscriptionName = firstRow.subscriptionName || ''
          }
        }
      } catch (error) {
        console.error('[SubscriptionReportsPrintable] Failed to load data', error)
        this.rows = []
        this.summary = null
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      return moment(date).tz(REPORT_TIMEZONE).format('DD. MM. YYYY')
    },
    formatDateTime(date) {
      return moment(date).tz(REPORT_TIMEZONE).format('DD. MM. YYYY HH:mm')
    },
    formatCurrency(value) {
      const formatter = new Intl.NumberFormat('hr-HR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
      })

      return formatter.format(value || 0)
    },
    formatNumber(value) {
      const formatter = new Intl.NumberFormat('hr-HR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })

      return formatter.format(value || 0)
    },
    printPage() {
      window.print()
    },
    goBack() {
      // Go back to the previous page or close if opened in new tab
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        window.close()
      }
    },
  },
}
</script>

<style scoped>
.print-page-container {
  min-height: 100vh;
  background: #f5f5f5;
}

.print-controls {
  position: sticky;
  top: 0;
  background: white;
  padding: 16px 32px;
  border-bottom: 1px solid #ddd;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 1000;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.print-button,
.back-button {
  width: 200px;
  min-height: 44px;
  padding: 12px 16px;
  border: 1px solid #0277bd;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s;
}
.print-button { background: #0277bd; color: white; }
.print-button:hover { background: #0288d1; }
.back-button { background: white; color: #0277bd; }
.back-button:hover { background: #eef7fc; }
@media (max-width: 600px) {
  .print-controls { padding: 12px 16px; }
  .print-button, .back-button { flex: 1 1 200px; }
}
@media print {
  .print-controls { display: none !important; }
}

.print-wrapper {
  font-family: 'Source Sans Pro', Arial, sans-serif;
  color: #333;
  padding: 32px;
  background: white;
  margin: 0 auto;
  max-width: 1200px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.print-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
}

.print-header__left {
  display: flex;
  gap: 16px;
  align-items: center;
}

.print-header__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.print-header__period {
  color: #666;
  font-size: 14px;
}

.print-header__generated {
  color: #666;
  font-size: 14px;
}

.summary-section h1 {
  font-size: 24px;
  margin-bottom: 16px;
}

.summary-section dl {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px 24px;
  margin-bottom: 32px;
}

.summary-section dt {
  font-weight: 600;
  color: #0277bd;
}

.summary-section dd {
  margin: 0;
}

.table-section h2 {
  font-size: 20px;
  margin-bottom: 16px;
}

.table-section table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.table-section th,
.table-section td {
  padding: 8px;
  border: 1px solid #d9d9d9;
  text-align: left;
}

.table-section th {
  background: #f5f5f5;
  font-weight: 600;
}

.empty-state,
.loading-state {
  font-size: 16px;
  color: #666;
}
</style>
